import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle,
  Circle,
  ArrowRight,
  Package,
  Users,
  BarChart3,
  Zap,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { FosButton, FosCard } from '@/components/ui/flipperos';
import { cn } from '@/lib/utils';

interface BusinessInfo {
  businessName: string;
  businessType: string;
  monthlyInventory: string;
  primaryFocus: string;
  description: string;
}

interface OnboardingProps {
  onComplete: () => void;
}

export default function Onboarding({ onComplete }: OnboardingProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    businessName: user?.businessName || '',
    businessType: '',
    monthlyInventory: '',
    primaryFocus: '',
    description: '',
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleBusinessInfoChange = (field: keyof BusinessInfo, value: string) => {
    setBusinessInfo((prev) => ({ ...prev, [field]: value }));
  };

  // Onboarding used to seed a placeholder "My First Item" inventory
  // row on step-3-next so the Dashboard wouldn't look empty. The
  // Dashboard now renders a proper FirstRunHero on count=0
  // (docs/UX_ANALYSIS.md fix #3), so the placeholder is dead weight —
  // it cluttered the user's actual inventory and short-circuited the
  // empty-state CTA. Removed.

  const markOnboardingComplete = async () => {
    const {
      data: { user: sbUser },
    } = await supabase.auth.getUser();
    if (!sbUser) return;

    // Persist the business-profile fields the user just entered alongside the
    // completion stamp. `business_name` is a real profiles column; the rest
    // (no dedicated columns) are stashed into the existing `preferences` Json.
    await supabase
      .from('profiles')
      .update({
        onboarding_completed_at: new Date().toISOString(),
        business_name: businessInfo.businessName || null,
        preferences: {
          businessType: businessInfo.businessType,
          monthlyInventory: businessInfo.monthlyInventory,
          primaryFocus: businessInfo.primaryFocus,
          description: businessInfo.description,
        },
      })
      .eq('id', sbUser.id);
  };

  const handleNext = async () => {
    if (step === totalSteps) {
      await markOnboardingComplete();
      onComplete();
    } else {
      setStep(step + 1);
    }
  };

  const handleSkip = async () => {
    // Skipping still stamps onboarding_completed_at so the user isn't
    // re-prompted, but we deliberately do NOT drop them on a bare
    // Dashboard with no next step. Connecting eBay is the single
    // highest-leverage first-run task (it unlocks listing, sync, comps,
    // and 1099-K), so a skipping user lands directly on the eBay-connect
    // surface. The Dashboard's connect-eBay CTAs (checklist, banner,
    // first-run hero) and the welcome tour entry point remain available —
    // none of them are gated on onboarding_completed_at — so the user is
    // never trapped.
    await markOnboardingComplete();
    onComplete();
    navigate('/settings/integrations');
  };

  const renderStep = () => {
    switch (step) {
      case 1: {
        const features = [
          {
            Icon: Package,
            title: 'Inventory Management',
            desc: 'Track your items, profits, and performance',
          },
          {
            Icon: Users,
            title: 'Customer Relations',
            desc: 'Manage customers and purchase history',
          },
          {
            Icon: BarChart3,
            title: 'Analytics & Insights',
            desc: 'AI-powered business intelligence',
          },
          {
            Icon: Zap,
            title: 'Automation Tools',
            desc: 'Streamline your workflows with rules and agents',
          },
        ];
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="mb-2 text-[22px] font-bold text-fos-text-primary">
                Welcome to For Visibility
              </h2>
              <p className="text-[13px] text-fos-text-muted">
                Your business operating system for flipping. Let's get you set up in just a few
                steps.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {features.map(({ Icon, title, desc }) => (
                <div
                  key={title}
                  className="rounded-fos-md border border-fos-border bg-fos-bg-elevated p-3.5"
                >
                  <div className="mb-1.5 flex items-center gap-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-fos border border-fos-accent-border bg-fos-accent-bg">
                      <Icon className="h-3.5 w-3.5 text-fos-accent" />
                    </span>
                    <h3 className="text-[13px] font-semibold text-fos-text-primary">{title}</h3>
                  </div>
                  <p className="text-[11px] text-fos-text-muted">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="mb-2 text-[22px] font-bold text-fos-text-primary">
                Tell us about your business
              </h2>
              <p className="text-[13px] text-fos-text-muted">
                This helps us customize For Visibility for your specific needs
              </p>
            </div>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="businessName" className="text-[12px] text-fos-text-secondary">
                  Business Name
                </Label>
                <Input
                  id="businessName"
                  value={businessInfo.businessName}
                  onChange={(e) => handleBusinessInfoChange('businessName', e.target.value)}
                  placeholder="Your business name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="businessType" className="text-[12px] text-fos-text-secondary">
                  Business Type
                </Label>
                <Select
                  value={businessInfo.businessType}
                  onValueChange={(v) => handleBusinessInfoChange('businessType', v)}
                >
                  <SelectTrigger id="businessType">
                    <SelectValue placeholder="Select your business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="antiques">Antiques Dealer</SelectItem>
                    <SelectItem value="thrift">Thrift Store</SelectItem>
                    <SelectItem value="reseller">Online Reseller</SelectItem>
                    <SelectItem value="auction">Auction House</SelectItem>
                    <SelectItem value="collector">Collector/Hobbyist</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="monthlyInventory" className="text-[12px] text-fos-text-secondary">
                  Monthly Inventory Volume
                </Label>
                <Select
                  value={businessInfo.monthlyInventory}
                  onValueChange={(v) => handleBusinessInfoChange('monthlyInventory', v)}
                >
                  <SelectTrigger id="monthlyInventory">
                    <SelectValue placeholder="How many items do you handle monthly?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1–10 items</SelectItem>
                    <SelectItem value="11-50">11–50 items</SelectItem>
                    <SelectItem value="51-200">51–200 items</SelectItem>
                    <SelectItem value="200+">200+ items</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 3: {
        const focusOptions = [
          {
            value: 'profit',
            label: 'Profit Maximization',
            desc: 'Focus on margins and pricing optimization',
          },
          {
            value: 'volume',
            label: 'High Volume Sales',
            desc: 'Streamline operations for quick turnover',
          },
          {
            value: 'specialty',
            label: 'Specialty Items',
            desc: 'Deep expertise in specific categories',
          },
          {
            value: 'marketplace',
            label: 'Multi-Channel Sales',
            desc: 'Selling across multiple platforms',
          },
        ];
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="mb-2 text-[22px] font-bold text-fos-text-primary">
                What's your primary focus?
              </h2>
              <p className="text-[13px] text-fos-text-muted">
                We'll highlight the most relevant features for your business model
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {focusOptions.map((option) => {
                const selected = businessInfo.primaryFocus === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleBusinessInfoChange('primaryFocus', option.value)}
                    className={cn(
                      'rounded-fos-md border bg-fos-bg-elevated p-3.5 text-left transition-colors duration-fos',
                      selected
                        ? 'border-fos-accent-border bg-fos-accent-bg'
                        : 'border-fos-border hover:border-fos-border-strong',
                    )}
                  >
                    <div className="mb-1.5 flex items-center gap-2">
                      {selected ? (
                        <CheckCircle className="h-4 w-4 text-fos-accent" />
                      ) : (
                        <Circle className="h-4 w-4 text-fos-text-muted" />
                      )}
                      <h3 className="text-[13px] font-semibold text-fos-text-primary">
                        {option.label}
                      </h3>
                    </div>
                    <p className="text-[11px] text-fos-text-muted">{option.desc}</p>
                  </button>
                );
              })}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-[12px] text-fos-text-secondary">
                Business Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={businessInfo.description}
                onChange={(e) => handleBusinessInfoChange('description', e.target.value)}
                placeholder="Tell us more about your business…"
                rows={3}
              />
            </div>
          </div>
        );
      }

      case 4:
        return (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="mb-2 text-[22px] font-bold text-fos-text-primary">You're all set</h2>
              <p className="text-[13px] text-fos-text-muted">
                For Visibility is now customized for your business. Here's what you can do first:
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FosCard className="border-fos-accent-border bg-fos-accent-bg p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4 text-fos-accent" />
                  <span className="text-[13px] font-semibold text-fos-text-primary">
                    Quick Start
                  </span>
                </div>
                <ul className="space-y-1 text-[12px] text-fos-text-secondary">
                  <li>✓ First inventory item created</li>
                  <li>○ Process a test sale</li>
                  <li>○ Explore your dashboard</li>
                </ul>
              </FosCard>
              <FosCard className="p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-fos-text-secondary" />
                  <span className="text-[13px] font-semibold text-fos-text-primary">Advanced</span>
                </div>
                <ul className="space-y-1 text-[12px] text-fos-text-secondary">
                  <li>• Customer management</li>
                  <li>• AI pricing suggestions</li>
                  <li>• Analytics &amp; reporting</li>
                </ul>
              </FosCard>
            </div>

            <FosCard className="border-fos-amber bg-fos-amber-bg p-4">
              <div className="flex items-start gap-3">
                <Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-fos-amber" />
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 text-[13px] font-semibold text-fos-amber">
                    Connect eBay to start listing
                  </h3>
                  <p className="mb-3 text-[11px] text-fos-text-secondary">
                    Link your eBay seller account to publish listings and sync sold orders
                    automatically.
                  </p>
                  <FosButton
                    variant="default"
                    size="xs"
                    onClick={() => navigate('/settings/integrations')}
                  >
                    Connect eBay
                    <ExternalLink className="h-3 w-3" />
                  </FosButton>
                </div>
              </div>
            </FosCard>

            <div className="rounded-fos-md border border-fos-border bg-fos-bg-elevated p-4">
              <h3 className="mb-1 text-[13px] font-semibold text-fos-text-primary">Pro Tip</h3>
              <p className="text-[12px] text-fos-text-muted">
                Add a few items to your inventory first. That populates your dashboard with real
                data and unlocks AI pricing recommendations.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fos-surface flex min-h-screen items-center justify-center bg-fos-bg-base p-4 font-sans text-fos-text-primary">
      <div className="w-full max-w-2xl rounded-fos-md border border-fos-border bg-fos-bg-card shadow-fos-lg">
        {/* Header — step indicator + skip + progress bar in fos- tokens. */}
        <div className="border-b border-fos-border px-6 py-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-fos-text-muted">
              Step {step} of {totalSteps}
            </span>
            <button
              type="button"
              onClick={handleSkip}
              className="text-[12px] text-fos-text-muted transition-colors duration-fos hover:text-fos-text-primary"
            >
              Skip setup
            </button>
          </div>
          {/* Progress bar — manual implementation matching the design's
              thin progress strip rather than shadcn's Progress (which
              would render with shadcn's color tokens, not fos-). */}
          <div className="h-1 w-full overflow-hidden rounded-full bg-fos-bg-elevated">
            <div
              className="h-full rounded-full bg-fos-accent transition-all duration-fos-slow"
              style={{ width: `${progress}%` }}
              aria-label={`Progress ${Math.round(progress)}%`}
            />
          </div>
        </div>

        <div className="p-6">
          {renderStep()}

          <div className="mt-8 flex justify-between gap-3">
            {step > 1 ? (
              <FosButton variant="default" size="md" onClick={() => setStep(step - 1)}>
                Back
              </FosButton>
            ) : (
              <div />
            )}

            <FosButton variant="primary" size="md" onClick={handleNext}>
              {step === totalSteps ? 'Get Started' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </FosButton>
          </div>
        </div>
      </div>
    </div>
  );
}
